import {useCallback, useEffect, useState} from "react";
import {Canvas, FabricObject} from "fabric";
import {FloorPlanItem} from "../types";
import {rebuildElement} from "../utils/fabric-factory";

interface Props {
    canvas: Canvas | null;
    element: FabricObject;
    onBack: () => void;
    onElementUpdated: () => void;
}

export const useEditConfiguration = ({
                                         canvas,
                                         element,
                                         onBack,
                                         onElementUpdated,
                                     }: Props) => {

    // Position state
    const [position, setPosition] = useState({
        x: element.left ?? 0,
        y: element.top ?? 0
    });

    // Rotation state
    const [rotation, setRotation] = useState(element.angle ?? 0);

    // Sync position/rotation when element changes
    useEffect(() => {
        setPosition({x: element.left ?? 0, y: element.top ?? 0});
        setRotation(element.angle ?? 0);
    }, [element]);

    // Sync position when element is moved on canvas
    const handleMoving = useCallback((e: any) => {
        if (e.target === element) {
            setPosition({x: e.target.left ?? 0, y: e.target.top ?? 0});
        }
    }, [element]);

    // Sync rotation when element is rotated on canvas
    const handleRotating = useCallback((e: any) => {
        if (e.target === element) {
            setRotation(e.target.angle ?? 0);
        }
    }, [element]);

    // Subscribe to canvas events
    useEffect(() => {
        if (!canvas) return;
        canvas.on('object:moving', handleMoving);
        canvas.on('object:rotating', handleRotating);
        return () => {
            canvas.off('object:moving', handleMoving);
            canvas.off('object:rotating', handleRotating);
        };
    }, [canvas, handleMoving, handleRotating]);

    // Rebuild element with new item data
    const rebuild = useCallback((newItem: FloorPlanItem) => {
        if (!canvas) return;
        rebuildElement(element, canvas, newItem);
        onElementUpdated();
    }, [canvas, element, onElementUpdated]);

    // Update position
    const handleUpdatePosition = useCallback((x: number, y: number) => {
        if (!canvas) return;
        element.set({left: x, top: y});
        element.setCoords();
        canvas.renderAll();
        setPosition({x, y});
        onElementUpdated();
    }, [canvas, element, onElementUpdated]);

    // Update rotation
    const handleRotationChange = useCallback((value: number | undefined) => {
        if (value === undefined || !canvas) return;
        const normalized = ((value % 360) + 360) % 360;
        element.set({angle: normalized});
        element.setCoords();
        canvas.renderAll();
        setRotation(normalized);
        onElementUpdated();
    }, [canvas, element, onElementUpdated]);

    // Handle selectable change (visual only - use rebuild to persist)
    const updateSelectable = useCallback((selectable: boolean) => {
        if (!canvas) return;
        element.set({
            selectable,
            evented: selectable,
            hasControls: selectable,
            hasBorders: selectable,
            lockMovementX: !selectable,
            lockMovementY: !selectable,
            hoverCursor: selectable ? 'move' : 'default',
        });
        element.setCoords();
        if (!selectable && canvas.getActiveObject() === element) {
            canvas.discardActiveObject();
        }
        canvas.renderAll();
    }, [canvas, element]);

    // Layer operations
    const handleBringToFront = useCallback(() => {
        if (!canvas) return;
        canvas.bringObjectToFront(element);
        canvas.renderAll();
        onElementUpdated();
    }, [canvas, element, onElementUpdated]);

    const handleSendToBack = useCallback(() => {
        if (!canvas) return;
        canvas.sendObjectToBack(element);
        canvas.renderAll();
        onElementUpdated();
    }, [canvas, element, onElementUpdated]);

    const handleBringForward = useCallback(() => {
        if (!canvas) return;
        canvas.bringObjectForward(element);
        canvas.renderAll();
        onElementUpdated();
    }, [canvas, element, onElementUpdated]);

    const handleSendBackward = useCallback(() => {
        if (!canvas) return;
        canvas.sendObjectBackwards(element);
        canvas.renderAll();
        onElementUpdated();
    }, [canvas, element, onElementUpdated]);

    // Delete element
    const handleDelete = useCallback(() => {
        if (!canvas) return;
        canvas.remove(element);
        canvas.discardActiveObject();
        canvas.renderAll();
        onBack();
        onElementUpdated();
    }, [canvas, element, onBack, onElementUpdated]);

    return {
        // Rebuild
        rebuild,
        // Position
        position,
        handleUpdatePosition,
        // Rotation
        rotation,
        handleRotationChange,
        // Selectable
        updateSelectable,
        // Layer
        handleBringToFront,
        handleSendToBack,
        handleBringForward,
        handleSendBackward,
        // Delete
        handleDelete,
    };
};
